import { List } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { LegalHeading } from "@/src/legal/utils/legal-markdown.utils";

interface LegalTocProps {
	headings: LegalHeading[];
}

const OBSERVER_MARGIN = "-96px 0px -70% 0px";

function TocList({ headings, activeId }: { headings: LegalHeading[]; activeId: string | null }) {
	return (
		<ol className="space-y-1">
			{headings.map((heading, index) => {
				const isActive = heading.id === activeId;
				return (
					<li key={heading.id}>
						<a
							href={`#${heading.id}`}
							aria-current={isActive ? "location" : undefined}
							className={`flex gap-3 rounded-lg px-3 py-1.5 text-sm transition ${
								isActive ? "bg-brand-50 font-medium text-brand-700" : "text-default-600 hover:bg-content2 hover:text-foreground"
							}`}
						>
							<span className={`tabular-nums ${isActive ? "text-brand-500" : "text-default-400"}`}>
								{String(index + 1).padStart(2, "0")}
							</span>
							<span className="text-pretty">{heading.text}</span>
						</a>
					</li>
				);
			})}
		</ol>
	);
}

export function LegalToc({ headings }: LegalTocProps) {
	const { t } = useTranslation("legal");
	const [activeId, setActiveId] = useState<string | null>(null);

	useEffect(() => {
		const elements = headings.map((heading) => document.getElementById(heading.id)).filter((element) => element !== null);
		if (elements.length === 0) return;

		const observer = new IntersectionObserver(
			(entries) => {
				const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
				const first = visible[0];
				if (first) setActiveId(first.target.id);
			},
			{ rootMargin: OBSERVER_MARGIN, threshold: 0 },
		);

		for (const element of elements) observer.observe(element);
		return () => observer.disconnect();
	}, [headings]);

	if (headings.length === 0) return null;

	return (
		<>
			<details className="legal-toc mb-8 rounded-2xl border border-default-200 bg-content1 lg:hidden">
				<summary className="flex cursor-pointer items-center gap-2 px-4 py-3 text-sm font-semibold text-foreground">
					<List className="h-4 w-4 text-brand-500" />
					{t("toc")}
				</summary>
				<div className="border-t border-default-200/60 px-2 py-2">
					<TocList headings={headings} activeId={activeId} />
				</div>
			</details>

			<nav aria-label={t("toc")} className="legal-toc hidden lg:sticky lg:top-24 lg:block lg:self-start">
				<p className="mb-3 px-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-default-500">{t("toc")}</p>
				<TocList headings={headings} activeId={activeId} />
			</nav>
		</>
	);
}
